// Import the RTK Query methods from the React-specific entry point
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';

import { SpecialEventStatusVariant } from 'appConstants';
import { SpecialDateRequest } from 'appTypes';
import { FeatureCollection, MultiPolygon, Point, Polygon } from 'geojson';

import { baseQuery } from './utils';

// https://redux.js.org/tutorials/essentials/part-7-rtk-query-basics
// https://redux-toolkit.js.org/rtk-query/usage/prefetching

export type UserProjectTemplate = {
  region_boundary: Polygon | MultiPolygon;
  template_name: string;
  project_max_area_mi2: number;
  active_project_quota: number;
  special_dates_quota: number;
  end_date: string;
  uses_metric: boolean;
  num_days_rolling: number;
  num_months_rolling: number;
};

export type ProjectStatusVariant =
  | 'draft'
  | 'pending_review'
  | 'fetching'
  | 'active'
  | 'inactive';

export type UserProject = {
  project_name: string;
  project_id: string;
  boundary: Polygon;
  status: string;
  project_end: string;
  uses_metric: boolean;
  special_date_requests?: SpecialDateRequest[];
  special_dates_quota: number;
  num_days_rolling: number;
  num_months_rolling: number;
};

export type UserProjectRequest = {
  hash_id: string;
  project_name: string;
  boundary: Polygon;
  subscription_id: string;
  is_draft: boolean;
  status: string;
  project_end: string;
  special_date_requests: SpecialDateRequest[];
};

export type UserProjectSubscription = {
  subscription_id: string;
  subscription_name: string;
  can_create: boolean;
  user_is_sub_admin: boolean;
  template: UserProjectTemplate;
  projects: UserProject[];
  project_requests: UserProjectRequest[];
  active_projects: number;
  used_quota: number;
};

export type BoundaryInfo = {
  boundary_geojson: FeatureCollection<Polygon>;
  boundary_centroid: Point;
  warnings: string[];
};

const serializeSpecialDateRequest = ({
  dates,
  name,
  comparison_month,
  date_range_start,
  date_range_end,
}: SpecialDateRequest) => ({
  dates,
  name,
  comparison_month,
  date_range_start,
  date_range_end,
});

// Define our single API slice object
export const userAdminApiSlice = createApi({
  reducerPath: 'userAdmin_api',
  baseQuery,
  tagTypes: ['subscriptions'],
  // The "endpoints" represent operations and requests for this server
  endpoints: (builder) => ({
    projectBoundaryProcess: builder.mutation<
      BoundaryInfo,
      {
        file: File;
      }
    >({
      query: ({ file }) => {
        const body = new FormData();
        body.append('Content-Type', file.type);
        body.append('boundary_file', file);

        return {
          url: `user_project_requests/boundary/`,
          method: 'POST',
          body,
        };
      },
      transformResponse: (rawResult: any, meta, arg) => {
        return {
          boundary_geojson: JSON.parse(rawResult.boundary_geojson),
          boundary_centroid: JSON.parse(rawResult.boundary_centroid),
          warnings: rawResult.warnings,
        };
      },
      invalidatesTags: [],
    }),

    getUserSubscriptions: builder.query<
      { subscriptions: UserProjectSubscription[] },
      void
    >({
      query: () => {
        return `subscriptions/`;
      },
      transformResponse: (rawResult: any, meta, arg) => {
        // console.log('getUserAdmins query results');
        return { subscriptions: rawResult };
      },
      providesTags: [{ type: 'subscriptions' }],
    }),
    specialEventDateCreate: builder.mutation<
      SpecialDateRequest,
      { project_slug: string; special_date_request: SpecialDateRequest }
    >({
      query: ({ project_slug, special_date_request }) => {
        {
          if (!project_slug) throw new Error('project_slug is required');
          return {
            url: `subscriptions/${project_slug}/special_date_requests/`,
            method: 'POST',
            body: serializeSpecialDateRequest(special_date_request),
          };
        }
      },
      invalidatesTags: [{ type: 'subscriptions' }],
    }),
    specialEventDateDelete: builder.mutation<
      { status: 'success' },
      { project_slug: string; hash_id: string }
    >({
      query: ({ project_slug, hash_id }) => {
        if (!project_slug || !hash_id)
          throw new Error('project_slug and hash_id are required');

        return {
          url: `subscriptions/${project_slug}/special_date_requests/${hash_id}/`,
          method: 'DELETE',
        };
      },
      invalidatesTags: [{ type: 'subscriptions' }],
    }),
    userProjectRequestCreate: builder.mutation<
      Partial<UserProjectRequest>,
      {
        project_name: string;
        boundary: Polygon;
        subscription_id: string;
        is_draft: boolean;
        project_end: string;
        special_date_requests: SpecialDateRequest[];
      }
    >({
      query: ({
        project_name,
        boundary,
        subscription_id,
        is_draft,
        project_end,
        special_date_requests,
      }) => {
        return {
          url: `user_project_requests/`,
          method: 'POST',
          body: {
            project_name,
            boundary,
            subscription_id,
            is_draft,
            project_end,
            special_date_requests: special_date_requests.map(
              serializeSpecialDateRequest,
            ),
          },
        };
      },
      transformResponse: (rawResult: any, meta, arg) => {
        return { hash_id: rawResult.hash_id };
      },
      invalidatesTags: [{ type: 'subscriptions' }],
    }),
    userProjectRequestUpdate: builder.mutation<
      {},
      {
        project_name: string;
        boundary: Polygon;
        subscription_id: string;
        is_draft: boolean;
        hash_id: string;
        project_end: string;
        special_date_requests: SpecialDateRequest[];
      }
    >({
      query: ({
        project_name,
        boundary,
        subscription_id,
        is_draft,
        hash_id,
        project_end,
        special_date_requests,
      }) => {
        return {
          url: `user_project_requests/${hash_id}/`,
          method: 'PUT',
          body: {
            project_name,
            boundary,
            subscription_id,
            is_draft,
            project_end,
            special_date_requests: special_date_requests.map(
              serializeSpecialDateRequest,
            ),
          },
        };
      },
      transformResponse: (rawResult: any, meta, arg) => {
        return {};
      },
      invalidatesTags: [{ type: 'subscriptions' }],
    }),
    userAdminDelete: builder.mutation<{}, { hash_id: string }>({
      query: ({ hash_id }) => {
        return {
          url: `user_project_requests/${hash_id}/`,
          method: 'DELETE',
        };
      },
      transformResponse: (rawResult: any, meta, arg) => {
        return {};
      },
      invalidatesTags: [{ type: 'subscriptions' }],
    }),
  }),
});

// Export the auto-generated hook for the `getDates` query endpoint
export const {
  useProjectBoundaryProcessMutation,
  useGetUserSubscriptionsQuery,
  useUserProjectRequestCreateMutation,
  useUserProjectRequestUpdateMutation,
  useUserAdminDeleteMutation,
  useSpecialEventDateCreateMutation,
  useSpecialEventDateDeleteMutation,
} = userAdminApiSlice;

export type InternalBoundaryPolygon = Polygon & { id?: string };

type SubscriptionSubState = {
  current_draft: {
    project_name: string;
    boundary: InternalBoundaryPolygon;
    hash_id: string;
    project_end: string;
    special_date_requests: SpecialDateRequest[];
  };
};
const initialState: {
  subscriptions: { [sub_id: string]: SubscriptionSubState };
  current_subscription_id: string;
} = { subscriptions: {}, current_subscription_id: undefined };

function ensureSubscriptionState(state, subscription_id) {
  if (!state.subscriptions[subscription_id]) {
    state.subscriptions[subscription_id] = {
      current_draft: {
        project_name: null,
        boundary: null,
        hash_id: undefined,
        project_end: null,
        special_date_requests: [],
      },
    };
  }
}

export const userAdminSlice = createSlice({
  name: 'userAdmins',
  initialState,
  reducers: {
    setDraftState: (
      state,
      action: PayloadAction<{
        project_name: string;
        boundary: Polygon;
        subscription_id: string;
        hash_id?: string;
        project_end: string;
        special_date_requests: SpecialDateRequest[];
      }>,
    ) => {
      const {
        subscription_id,
        project_name,
        boundary,
        hash_id,
        project_end,
        special_date_requests,
      } = action.payload;
      ensureSubscriptionState(state, subscription_id);
      state.subscriptions[subscription_id].current_draft.project_name =
        project_name;
      state.subscriptions[subscription_id].current_draft.boundary = boundary;
      state.subscriptions[subscription_id].current_draft.hash_id = hash_id;
      state.subscriptions[subscription_id].current_draft.project_end =
        project_end;
      state.current_subscription_id = subscription_id;
      state.subscriptions[subscription_id].current_draft.special_date_requests =
        special_date_requests;
    },
    setDraftBoundary: (
      state,
      action: PayloadAction<{
        boundary: Polygon;
        subscription_id: string;
      }>,
    ) => {
      const { subscription_id, boundary } = action.payload;
      ensureSubscriptionState(state, subscription_id);
      state.subscriptions[subscription_id].current_draft.boundary = boundary;
      state.current_subscription_id = subscription_id;
    },
    setDraftProjectName: (
      state,
      action: PayloadAction<{
        project_name: string;
        subscription_id: string;
      }>,
    ) => {
      const { subscription_id, project_name } = action.payload;
      ensureSubscriptionState(state, subscription_id);
      state.subscriptions[subscription_id].current_draft.project_name =
        project_name;
      state.current_subscription_id = subscription_id;
    },
    setDraftProjectEnd: (
      state,
      action: PayloadAction<{
        project_end: string;
        subscription_id: string;
      }>,
    ) => {
      const { subscription_id, project_end } = action.payload;
      ensureSubscriptionState(state, subscription_id);
      state.subscriptions[subscription_id].current_draft.project_end =
        project_end;
      state.current_subscription_id = subscription_id;
    },
    addDraftSpecialDate: (
      state,
      action: PayloadAction<{
        subscription_id: string;
        date_request: SpecialDateRequest;
      }>,
    ) => {
      const { subscription_id, date_request } = action.payload;
      state.subscriptions[
        subscription_id
      ].current_draft.special_date_requests.unshift({
        ...date_request,
        status: SpecialEventStatusVariant.requested,
      });
    },
    updateDraftSpecialDate: (
      state,
      action: PayloadAction<{
        subscription_id: string;
        date_request: SpecialDateRequest;
      }>,
    ) => {
      const { subscription_id, date_request } = action.payload;
      const list =
        state.subscriptions[subscription_id].current_draft
          .special_date_requests;
      let index;

      if (date_request.hash_id)
        index = list.findIndex((req) => req.hash_id === date_request.hash_id);
      else if (date_request.tempId)
        index = list.findIndex((req) => req.tempId === date_request.tempId);
      else return;
      if (index === -1) return;

      list[index] = {
        ...list[index],
        ...date_request,
      };
    },
    deleteDraftEditableSpecialDate: (
      state,
      action: PayloadAction<{
        subscription_id: string;
        hash_id: string;
      }>,
    ) => {
      const { subscription_id, hash_id } = action.payload;

      if (!hash_id) return;
      state.subscriptions[subscription_id].current_draft.special_date_requests =
        state.subscriptions[
          subscription_id
        ].current_draft.special_date_requests.filter(
          (req) => req.hash_id !== hash_id,
        );
    },
    deleteDraftNewSpecialDate: (
      state,
      action: PayloadAction<{
        subscription_id: string;
        tempId: string;
      }>,
    ) => {
      const { subscription_id, tempId } = action.payload;
      if (!tempId) return;
      state.subscriptions[subscription_id].current_draft.special_date_requests =
        state.subscriptions[
          subscription_id
        ].current_draft.special_date_requests.filter(
          (req) => req.tempId !== tempId,
        );
    },
    setCurrentSubscriptionId: (state, action) => {
      state.current_subscription_id = action.payload;
    },
    resetDraftState: (state) => {
      ensureSubscriptionState(state, state.current_subscription_id);
      state.subscriptions[
        state.current_subscription_id
      ].current_draft.project_name = undefined;
      state.subscriptions[
        state.current_subscription_id
      ].current_draft.boundary = undefined;
      state.subscriptions[
        state.current_subscription_id
      ].current_draft.project_end = undefined;
      state.subscriptions[state.current_subscription_id].current_draft.hash_id =
        undefined;
      state.subscriptions[
        state.current_subscription_id
      ].current_draft.special_date_requests = [];
    },
  },
});

const { actions } = userAdminSlice;
export const {
  setDraftState,
  resetDraftState,
  setCurrentSubscriptionId,
  setDraftBoundary,
  setDraftProjectName,
  setDraftProjectEnd,
  addDraftSpecialDate,
  deleteDraftNewSpecialDate,
  deleteDraftEditableSpecialDate,
  updateDraftSpecialDate,
} = actions;

export function getUserAdminState(rootState) {
  return rootState[userAdminSlice.name];
}

export function selectCurrentDraftState(rootState) {
  const state = getUserAdminState(rootState);
  return state.subscriptions[state.current_subscription_id]?.current_draft;
}

export function selectCurrentProjectName(rootState): string {
  return selectCurrentDraftState(rootState)?.project_name;
}

export function selectCurrentProjectEnd(rootState): string {
  return selectCurrentDraftState(rootState)?.project_end;
}

export function selectCurrentBoundary(rootState): Polygon {
  return selectCurrentDraftState(rootState)?.boundary;
}

export function selectCurrentHashId(rootState): string {
  return selectCurrentDraftState(rootState)?.hash_id;
}

export function selectSpecialEventDates(rootState): SpecialDateRequest[] {
  return selectCurrentDraftState(rootState)?.special_date_requests;
}
